import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, FloppyDisk } from "@phosphor-icons/react";
import api from "@/lib/api";

const inputCls = "w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]";
// Must match the section keys in QuoteBuilder.js (SECTIONS) so products
// created here show up in the right section of the proposal line-item
// picker. Historical typo "modules" → "optional_modules" migrated via
// UPDATE in backend/scripts (Feb 2026).
const CATEGORIES = ["user_licenses", "optional_modules", "implementation_fees", "services"];
const PRICING = ["monthly", "annual", "one_off"];
const CURRENCIES = ["USD", "GBP", "EUR", "AUD", "CAD"];

export default function ProductForm() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!productId;
  const [form, setForm] = useState({
    product_code: "", name: "", description: "", category: "user_licenses",
    pricing_type: "monthly", current_selling_price: 0, price_currency_code: "USD",
    unit_of_measure: "unit", is_active: true, notes: "",
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/mgmt/products/${productId}`).then(r => {
      const p = r.data;
      setForm({
        product_code: p.product_code || "", name: p.name || "", description: p.description || "",
        category: p.category || "user_licenses", pricing_type: p.pricing_type || "monthly",
        current_selling_price: p.current_selling_price || 0, price_currency_code: p.price_currency_code || "USD",
        unit_of_measure: p.unit_of_measure || "unit", is_active: p.is_active !== false, notes: p.notes || "",
      });
    }).finally(() => setLoading(false));
  }, [productId, isEdit]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_code || !form.name) { setError("Product code and name are required"); return; }
    if (!(form.description || "").trim()) { setError("Description is required — it appears on every proposal line."); return; }
    setSaving(true); setError("");
    try {
      if (isEdit) await api.put(`/mgmt/products/${productId}`, form);
      else await api.post("/mgmt/products", form);
      navigate("/mgmt/products");
    } catch (err) { setError(err.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  if (loading) return <div className="p-6"><div className="h-4 bg-[#F5F5F5] animate-pulse w-48" /></div>;

  return (
    <div className="p-6 max-w-2xl" data-testid="product-form-page">
      <Link to="/mgmt/products" className="inline-flex items-center gap-1.5 text-sm text-[#A3A3A3] hover:text-[#0A0A0A] mb-5"><ArrowLeft size={15} /> Back to Products</Link>
      <h1 className="text-2xl font-black text-[#0A0A0A] tracking-tight mb-6">{isEdit ? "Edit Product" : "New Product"}</h1>

      <form onSubmit={submit} className="space-y-5">
        <div className="bg-white border border-[#E5E5E5] p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Product Code <span className="text-red-500">*</span></label>
              <input value={form.product_code} onChange={e => set("product_code", e.target.value)} className={inputCls} disabled={isEdit} data-testid="product-code-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Name <span className="text-red-500">*</span></label>
              <input value={form.name} onChange={e => set("name", e.target.value)} className={inputCls} data-testid="product-name-input" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Description <span className="text-[#DC2626]">*</span></label>
              <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2} required className={inputCls} placeholder="Shown on every proposal line — write it like the customer will read it" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Category</label>
              <select value={form.category} onChange={e => set("category", e.target.value)} className={inputCls}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Pricing Type</label>
              <select value={form.pricing_type} onChange={e => set("pricing_type", e.target.value)} className={inputCls}>
                {PRICING.map(p => <option key={p} value={p}>{p.replace("_", " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Price</label>
              <input type="number" step="0.01" value={form.current_selling_price} onChange={e => set("current_selling_price", parseFloat(e.target.value) || 0)} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Currency</label>
              <select value={form.price_currency_code} onChange={e => set("price_currency_code", e.target.value)} className={inputCls}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Unit</label>
              <input value={form.unit_of_measure} onChange={e => set("unit_of_measure", e.target.value)} className={inputCls} />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <input type="checkbox" id="is_active" checked={form.is_active} onChange={e => set("is_active", e.target.checked)} />
              <label htmlFor="is_active" className="text-sm text-[#525252]">Active</label>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Notes</label>
              <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} className={inputCls} />
            </div>
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{error}</div>}

        <div className="flex gap-3">
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0055FF] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50" data-testid="save-product-button">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Product"}
          </button>
          <Link to="/mgmt/products" className="px-5 py-2.5 text-sm font-bold border border-[#E5E5E5] hover:bg-[#F5F5F5] uppercase tracking-wider">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
